import { CheckCircle2, Clock, Loader2, ShieldAlert } from "lucide-react";
import classnames from "classnames";
import InputField from "@components/fields/input-field";
import { ComponentSpinner } from "@components/spinner";
import usePublisherRouteGuard from "../use-publisher-route-guard";
import usePublisherKyc, { KYC_PHASE } from "./use-publisher-kyc";
import KycSummary from "./components/kyc-summary";
import KycVerifying from "./components/kyc-verifying";

const STEPS = ["Personal", "Tax details", "Bank"];

const PublisherKyc = () => {
    const { publisherId } = usePublisherRouteGuard();
    const {
        kyc, rejected, ready, phase, control, errors,
        step, done, next, previous, submitting,
        fullName, pan, gst, accountNumber, ifsc, accountHolder
    } = usePublisherKyc(publisherId);

    if (!ready) {
        return (
            <div className="flex justify-center py-24">
                <ComponentSpinner />
            </div>
        );
    }

    if (phase === KYC_PHASE.VERIFYING) {
        return <KycVerifying steps={done.map((s) => STEPS[s - 1])} />;
    }

    if (kyc.kyc_status === "verified") {
        return (
            <div className="p-6">
                <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
                    <CheckCircle2 className="mx-auto h-10 w-10 text-success" />
                    <h2 className="mt-3 text-xl font-semibold text-gray-900">You&apos;re KYC verified</h2>
                    <p className="mt-1 text-sm text-muted">Your payouts will process automatically each cycle.</p>
                    <KycSummary docs={kyc.kyc_docs} />
                </div>
            </div>
        );
    }

    // Anything rejected falls through to the wizard below so it can be corrected
    // and resubmitted — only an untouched submission sits in review.
    if (!rejected.length && kyc.kyc_docs?.bank_account) {
        return (
            <div className="p-6">
                <div className="rounded-xl border border-gray-200 bg-white p-10 text-center shadow-sm">
                    <div className="relative mx-auto flex h-10 w-10 items-center justify-center">
                        <span className="absolute h-10 w-10 animate-pulse rounded-full bg-accent/20" />
                        <Clock className="relative h-6 w-6 text-accent" />
                    </div>
                    <h2 className="mt-3 text-xl font-semibold text-gray-900">KYC submitted</h2>
                    <p className="mt-1 text-sm text-muted">Your documents are under review. We&apos;ll notify you once verified.</p>
                    <KycSummary docs={kyc.kyc_docs} />
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <h1 className="text-2xl font-semibold tracking-tight">KYC</h1>
            {rejected.length > 0 && (
                <div className="mt-3 flex gap-3 rounded-lg border border-danger/30 bg-danger/5 p-4">
                    <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
                    <div>
                        <p className="text-sm font-semibold text-gray-900">
                            {rejected.length === 1 ? "A detail was rejected" : "Some details were rejected"}
                        </p>
                        <p className="mt-0.5 text-sm text-muted">
                            Please fill in {rejected.map((s) => STEPS[s - 1]).join(" and ")} again and resubmit.
                        </p>
                    </div>
                </div>
            )}
            {kyc.days_remaining !== null && kyc.days_remaining !== undefined && (
                <p className="mt-1 text-sm text-muted">KYC window: {kyc.days_remaining} of {kyc.kyc_window_days} days remaining</p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
                {STEPS.map((label, idx) => {
                    const isRejected = rejected.includes(idx + 1);
                    return (
                        <div key={label} className="flex items-center gap-2">
                            <span
                                className={classnames(
                                    "grid h-7 w-7 place-items-center rounded-full text-xs font-semibold",
                                    isRejected
                                        ? "bg-danger text-white"
                                        : step > idx + 1 ? "bg-success text-white" : step === idx + 1 ? "bg-primary text-white" : "bg-gray-100 text-muted"
                                )}>
                                {isRejected ? "!" : step > idx + 1 ? "✓" : idx + 1}
                            </span>
                            <span className={step === idx + 1 ? "text-sm font-medium text-gray-900" : "text-sm text-muted"}>{label}</span>
                            {idx < STEPS.length - 1 && <div className="mx-2 h-px w-8 bg-gray-200" />}
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                {step === 1 && (
                    <div className="space-y-4">
                        <h2 className="font-semibold text-gray-900">Personal details</h2>
                        <InputField control={control} name="full_name" label="Full name" required disabled errors={errors} />
                        <InputField
                            control={control}
                            name="pan"
                            label="PAN number"
                            placeholder="ABCDE1234F"
                            required
                            errors={errors}
                            customOnChange={(value) => value.toUpperCase()} />
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-4">
                        <h2 className="font-semibold text-gray-900">Tax details</h2>
                        <InputField control={control} name="gst" label="GST number (optional)" placeholder="22AAAAA0000A1Z5" errors={errors} />
                    </div>
                )}

                {step === 3 && (
                    <div className="space-y-4">
                        <h2 className="font-semibold text-gray-900">Bank account</h2>
                        <InputField
                            control={control}
                            name="account_number"
                            label="Account number"
                            required
                            errors={errors}
                            inputMode="numeric"
                            customOnChange={(value) => value.replace(/[^0-9]/g, "")} />
                        <InputField
                            control={control}
                            name="account_number_confirm"
                            label="Confirm account number"
                            required
                            errors={errors}
                            inputMode="numeric"
                            customOnChange={(value) => value.replace(/[^0-9]/g, "")}
                            onPaste={(e) => e.preventDefault()} />
                        <InputField control={control} name="ifsc" label="IFSC code" placeholder="HDFC0001234" required errors={errors} />
                        <InputField control={control} name="account_holder" label="Account holder name" required errors={errors} />
                        <dl className="grid grid-cols-2 gap-y-2 rounded-md border border-gray-200 p-4 text-sm">
                            <dt className="text-muted">Full name</dt><dd className="font-medium text-gray-900">{fullName || "—"}</dd>
                            <dt className="text-muted">PAN</dt><dd className="font-medium text-gray-900">{pan || "—"}</dd>
                            <dt className="text-muted">GST</dt><dd className="font-medium text-gray-900">{gst || "Not provided"}</dd>
                            <dt className="text-muted">Bank</dt><dd className="font-medium text-gray-900">{accountHolder || "—"} · {ifsc || "—"}</dd>
                            <dt className="text-muted">Account no.</dt><dd className="font-medium text-gray-900">{accountNumber || "—"}</dd>
                        </dl>
                    </div>
                )}

                <div className="mt-6 flex justify-between">
                    <button
                        type="button"
                        onClick={previous}
                        disabled={step === 1 || submitting}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={next}
                        disabled={submitting}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-70">
                        {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                        {step < 3 ? "Next" : rejected.length ? "Resubmit KYC" : "Submit KYC"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PublisherKyc;
